import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation } from "@apollo/client/react";
import type { ClientDoNotContactReason } from "@athena/common/graphql/generated/schema";
import { OutreachQueueDocument, SetClientOutreachStatusDocument } from "@/graphql/clients.generated";

/**
 * Mark a client as lost contact, with the coordinator's reason, from the queue or the profile.
 *
 * The mutation returns the client, so the profile and directory update from the cache on their own;
 * only the queue is refetched, since a client marked lost leaves it rather than changing in place.
 *
 * Rejects when the save fails, after saying so, so the dialog that called it keeps the reason typed.
 */
export const useMarkLostContact = () => {
    const { t } = useTranslation();
    const [setClientOutreachStatus] = useMutation(SetClientOutreachStatusDocument);

    return async (clientId: string, clientName: string, reason: string): Promise<void> => {
        try {
            await setClientOutreachStatus({
                variables: { clientId, status: "LOST_CONTACT", reason },
                refetchQueries: [OutreachQueueDocument],
                awaitRefetchQueries: true,
            });
        } catch (error) {
            toast.error(t("admin.alumni.lostContact.failed", { name: clientName }));
            throw error;
        }
        toast.success(t("admin.alumni.lostContact.marked", { name: clientName }));
    };
};

/**
 * The way back: return a client marked lost contact to ACTIVE, which clears the reason. They are
 * due again by the usual rule, so the queue is refetched to show them; the profile updates from the
 * mutation's result.
 *
 * Rejects when the save fails, after saying so.
 */
export const useReturnToOutreach = () => {
    const { t } = useTranslation();
    const [setClientOutreachStatus] = useMutation(SetClientOutreachStatusDocument);

    return async (clientId: string, clientName: string): Promise<void> => {
        try {
            await setClientOutreachStatus({
                variables: { clientId, status: "ACTIVE", reason: null },
                refetchQueries: [OutreachQueueDocument],
                awaitRefetchQueries: true,
            });
        } catch (error) {
            toast.error(t("admin.alumni.returnToOutreach.failed", { name: clientName }));
            throw error;
        }
        toast.success(t("admin.alumni.returnToOutreach.returned", { name: clientName }));
    };
};

/**
 * The profile's do-not-contact dropdown: mark a client do not contact for a reason, or with `null`
 * take them off it, back to ACTIVE. The queue is refetched, since either way they leave or may join it.
 *
 * Rejects when the save fails, after saying so.
 */
export const useSetDoNotContact = () => {
    const { t } = useTranslation();
    const [setClientOutreachStatus] = useMutation(SetClientOutreachStatusDocument);

    return async (clientId: string, clientName: string, reason: ClientDoNotContactReason | null): Promise<void> => {
        try {
            await setClientOutreachStatus({
                variables: reason
                    ? { clientId, status: "DO_NOT_CONTACT", doNotContactReason: reason }
                    : { clientId, status: "ACTIVE" },
                refetchQueries: [OutreachQueueDocument],
                awaitRefetchQueries: true,
            });
        } catch (error) {
            toast.error(t("admin.alumni.doNotContact.failed", { name: clientName }));
            throw error;
        }
        toast.success(
            reason
                ? t("admin.alumni.doNotContact.marked", {
                      name: clientName,
                      reason: t(`admin.alumni.doNotContact.reason.${reason}`),
                  })
                : t("admin.alumni.doNotContact.cleared", { name: clientName }),
        );
    };
};
